import { type FormEvent, useEffect, useMemo, useState } from "react";

import FinanceFilterField from "../../components/finance/FinanceFilterField";
import FinanceMultiSelect from "../../components/finance/FinanceMultiSelect";
import FinanceFilterPanel from "../../components/finance/FinanceFilterPanel";
import FinanceHelpLabel from "../../components/finance/FinanceHelpLabel";
import FinanceIncompleteBadge from "../../components/finance/FinanceIncompleteBadge";
import FinanceMetricCard from "../../components/finance/FinanceMetricCard";
import FinancePagination from "../../components/finance/FinancePagination";
import FinanceWarningBanner from "../../components/finance/FinanceWarningBanner";
import TableLoadingSkeleton from "../../components/shared/TableLoadingSkeleton";
import CopyableIdentifier, {
  CopyIdentifierButton,
} from "../../components/controls/CopyableIdentifier";
import { formatMoney, knownMoney } from "../../finance/formatting";
import { fetchFinanceInventory } from "../../finance/inventory-api";
import type {
  FinanceInventoryFilters,
  FinanceInventoryGroup,
  FinanceInventoryItem,
  FinanceInventoryResponse,
  FinanceInventoryStage,
  FinanceInventoryStageSummary,
} from "../../finance/inventory-types";

const initialFilters: FinanceInventoryFilters = {
  groupBy: "stage",
  stages: [],
  classIds: [],
  categoryIds: [],
  productIds: [],
  warehouses: [],
  locations: [],
  rooms: [],
  valuationStatuses: [],
  units: [],
};

const stageLabels: Record<FinanceInventoryStage, string> = {
  raw: "Raw / Direct Received",
  open_wip: "Open WIP",
  finished: "Finished Goods",
};

function stageValue(stage: FinanceInventoryStageSummary | undefined) {
  return stage ? knownMoney(stage.value, stage.known_value) : "—";
}

function isGroup(
  row: FinanceInventoryItem | FinanceInventoryGroup,
): row is FinanceInventoryGroup {
  return "group_type" in row;
}

export default function FinanceInventory() {
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] =
    useState<FinanceInventoryFilters>(initialFilters);
  const [report, setReport] = useState<FinanceInventoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    fetchFinanceInventory(page, search, filters)
      .then((response) => active && setReport(response))
      .catch(() => {
        if (active) setError("Current inventory value could not be loaded.");
      })
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [page, search, filters]);

  const updateFilter = <K extends keyof FinanceInventoryFilters>(
    key: K,
    value: FinanceInventoryFilters[K],
  ) => {
    setFilters((current) => {
      const next = { ...current, [key]: value };
      if (key === "classIds") {
        next.categoryIds = [];
        next.productIds = [];
      }
      if (key === "categoryIds") next.productIds = [];
      if (key === "warehouses") next.locations = [];
      return next;
    });
    setPage(1);
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchDraft.trim());
    setPage(1);
  };

  const moreCount = [
    filters.warehouses,
    filters.locations,
    filters.rooms,
    filters.valuationStatuses,
    filters.units,
  ].filter((value) => value.length > 0).length;
  const hasFilters =
    search !== "" ||
    filters.groupBy !== initialFilters.groupBy ||
    Object.entries(filters).some(
      ([key, value]) =>
        key !== "groupBy" && Array.isArray(value) && value.length > 0,
    );
  const categories = useMemo(
    () =>
      report?.filters.categories.filter(
        (option) =>
          filters.classIds.length === 0 ||
          (option.class_id !== null &&
            filters.classIds.includes(option.class_id)),
      ) ?? [],
    [report, filters.classIds],
  );
  const products = useMemo(
    () =>
      report?.filters.products.filter(
        (option) =>
          (filters.classIds.length === 0 ||
            (option.class_id !== null &&
              filters.classIds.includes(option.class_id))) &&
          (filters.categoryIds.length === 0 ||
            (option.category_id !== null &&
              filters.categoryIds.includes(option.category_id))),
      ) ?? [],
    [report, filters.classIds, filters.categoryIds],
  );
  const locations = useMemo(
    () =>
      report?.filters.locations.filter(
        (option) =>
          filters.warehouses.length === 0 ||
          (option.warehouse_name !== null &&
            filters.warehouses.includes(option.warehouse_name)),
      ) ?? [],
    [report, filters.warehouses],
  );
  const summary = report?.summary;

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <div>
          <p className="text-sm font-medium text-emerald-700">Finance</p>
          <h1 className="text-2xl font-semibold text-slate-900">
            Inventory Value
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Current material value by inventory stage. This page is not affected
            by historical date filters.
          </p>
        </div>

        {summary && summary.missing_cost_items > 0 && (
          <div className="mt-5">
            <FinanceWarningBanner>
              {summary.missing_cost_items} current inventory row
              {summary.missing_cost_items === 1 ? " is" : "s are"} missing the
              cost required for valuation. Known value is shown separately.
            </FinanceWarningBanner>
          </div>
        )}

        <section className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <FinanceMetricCard
            label="Total Inventory Value"
            value={
              summary
                ? knownMoney(summary.total_value, summary.known_total_value)
                : "—"
            }
            incomplete={summary?.complete === false}
          />
          <FinanceMetricCard
            label="Raw / Direct Received"
            value={stageValue(summary?.stages.raw)}
            incomplete={summary?.stages.raw.complete === false}
          />
          <FinanceMetricCard
            label="Open WIP"
            value={stageValue(summary?.stages.open_wip)}
            incomplete={summary?.stages.open_wip.complete === false}
          />
          <FinanceMetricCard
            label="Finished Goods"
            value={stageValue(summary?.stages.finished)}
            incomplete={summary?.stages.finished.complete === false}
          />
        </section>

        <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <FinanceFilterPanel
            additionalFilterCount={moreCount}
            hasActiveFilters={hasFilters}
            onClear={() => {
              setFilters(initialFilters);
              setSearch("");
              setSearchDraft("");
              setPage(1);
            }}
            primary={
              <>
                <FinanceFilterField label="Grouping">
                  <select
                    value={filters.groupBy}
                    onChange={(event) =>
                      updateFilter(
                        "groupBy",
                        event.target
                          .value as FinanceInventoryFilters["groupBy"],
                      )
                    }
                  >
                    <option value="stage">Inventory stage</option>
                    <option value="class">Class</option>
                    <option value="category">Category</option>
                    <option value="product">Product</option>
                    <option value="location">Warehouse / location</option>
                    <option value="item">Individual inventory</option>
                  </select>
                </FinanceFilterField>
                <FinanceFilterField label="Inventory stage">
                  <FinanceMultiSelect<FinanceInventoryStage>
                    value={filters.stages}
                    placeholder="All stages"
                    options={[
                      { label: "Raw / Direct Received", value: "raw" },
                      { label: "Open WIP", value: "open_wip" },
                      { label: "Finished Goods", value: "finished" },
                    ]}
                    onChange={(value) => updateFilter("stages", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Class">
                  <FinanceMultiSelect
                    value={filters.classIds}
                    placeholder="All classes"
                    options={
                      report?.filters.classes.map((option) => ({
                        label: option.name,
                        value: option.id,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("classIds", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Category">
                  <FinanceMultiSelect
                    value={filters.categoryIds}
                    placeholder="All categories"
                    options={categories.map((option) => ({
                      label: option.name,
                      value: option.id,
                    }))}
                    onChange={(value) => updateFilter("categoryIds", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Product">
                  <FinanceMultiSelect
                    value={filters.productIds}
                    placeholder="All products"
                    options={products.map((option) => ({
                      label: option.name,
                      value: option.id,
                    }))}
                    onChange={(value) => updateFilter("productIds", value)}
                  />
                </FinanceFilterField>
              </>
            }
            additional={
              <>
                <FinanceFilterField label="Warehouse">
                  <FinanceMultiSelect
                    value={filters.warehouses}
                    placeholder="All warehouses"
                    options={
                      report?.filters.warehouses.map((name) => ({
                        label: name,
                        value: name,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("warehouses", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Location">
                  <FinanceMultiSelect
                    value={filters.locations}
                    placeholder="All locations"
                    options={locations.map((option) => ({
                      label: option.name,
                      value: option.name,
                    }))}
                    onChange={(value) => updateFilter("locations", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="WIP room">
                  <FinanceMultiSelect
                    value={filters.rooms}
                    placeholder="All rooms"
                    options={
                      report?.filters.rooms.map((name) => ({
                        label: name,
                        value: name,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("rooms", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Valuation status">
                  <FinanceMultiSelect<"complete" | "incomplete">
                    value={filters.valuationStatuses}
                    placeholder="All statuses"
                    options={[
                      { label: "Complete", value: "complete" },
                      { label: "Missing costs", value: "incomplete" },
                    ]}
                    onChange={(value) =>
                      updateFilter("valuationStatuses", value)
                    }
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Unit">
                  <FinanceMultiSelect<"kg" | "pieces">
                    value={filters.units}
                    placeholder="All units"
                    options={[
                      { label: "kg", value: "kg" },
                      { label: "pieces", value: "pieces" },
                    ]}
                    onChange={(value) => updateFilter("units", value)}
                  />
                </FinanceFilterField>
              </>
            }
          />

          <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">
                Current inventory detail
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                On-hand quantities and current editable material costs.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <form className="flex gap-2" onSubmit={submitSearch}>
                <input
                  aria-label="Search inventory"
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Product, lot, WIP, or location"
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                />
                <button
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                  type="submit"
                >
                  Search
                </button>
              </form>
            </div>
          </div>

          {error && (
            <p className="p-5 text-sm font-medium text-red-700">{error}</p>
          )}
          {!error && loading && (
            <div className="p-3">
              <TableLoadingSkeleton rows={7} columns={6} />
            </div>
          )}
          {!error && !loading && report?.data.length === 0 && (
            <p className="p-5 text-sm text-slate-500">
              No current inventory rows match these filters.
            </p>
          )}
          {!error && !loading && report && report.data.length > 0 && (
            <>
              <div className="overflow-x-auto">
                {report.group_by === "item" ? (
                  <ItemTable
                    rows={report.data.filter(
                      (row): row is FinanceInventoryItem => !isGroup(row),
                    )}
                  />
                ) : (
                  <GroupTable rows={report.data.filter(isGroup)} />
                )}
              </div>
              <FinancePagination
                label={report.group_by === "item" ? "rows" : "groups"}
                page={report.pagination.current_page}
                lastPage={report.pagination.last_page}
                total={report.pagination.total}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>
    </main>
  );
}

function GroupTable({ rows }: { rows: FinanceInventoryGroup[] }) {
  return (
    <table className="min-w-full divide-y divide-slate-200 text-sm">
      <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
        <tr>
          <th className="px-4 py-3">
            <FinanceHelpLabel label="Group" />
          </th>
          <th className="px-4 py-3">
            <FinanceHelpLabel label="Items" />
          </th>
          <th className="px-4 py-3">
            <FinanceHelpLabel label="On Hand" />
          </th>
          <th className="px-4 py-3">
            <FinanceHelpLabel label="Value" />
          </th>
          <th className="px-4 py-3">
            <FinanceHelpLabel label="Status" />
          </th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((row) => (
          <tr key={row.key}>
            <td className="px-4 py-4 font-semibold text-slate-900">
              {row.group_name}
            </td>
            <td className="px-4 py-4">{row.item_count}</td>
            <td className="px-4 py-4">
              {row.quantity.toLocaleString("en-CA")} {row.unit}
            </td>
            <td className="px-4 py-4 font-medium">
              {knownMoney(row.value, row.known_value)}
            </td>
            <td className="px-4 py-4">
              {row.complete ? (
                <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                  Complete
                </span>
              ) : (
                <FinanceIncompleteBadge count={row.missing_cost_items} />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ItemTable({ rows }: { rows: FinanceInventoryItem[] }) {
  return (
    <table className="min-w-full divide-y divide-slate-200 text-sm">
      <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
        <tr>
          {[
            "Stage",
            "Product / Lot",
            "WIP",
            "Location",
            "On Hand",
            "Value",
            "Status",
          ].map((label) => (
            <th key={label} className="px-4 py-3">
              <FinanceHelpLabel label={label} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((item) => (
          <tr key={`${item.stage}-${item.inventory_id}`}>
            <td className="px-4 py-4">{stageLabels[item.stage]}</td>
            <td className="px-4 py-4">
              <p className="font-semibold text-slate-900">
                {item.product_name || item.plu}
              </p>
              <p className="flex items-center gap-1 text-xs text-slate-500">
                <span>{item.plu} · Lot</span>
                <CopyableIdentifier
                  value={item.lot_number}
                  description="lot number"
                />
              </p>
            </td>
            <td className="px-4 py-4">
              {item.wip_id ? (
                <span className="relative inline-block pr-4">
                  <a
                    className="font-medium text-emerald-700 hover:underline"
                    href={`/wips/${item.wip_id}`}
                  >
                    {item.wip_lot_number || `#${item.wip_id}`}
                  </a>
                  <CopyIdentifierButton
                    value={item.wip_lot_number}
                    description="lot number"
                    wrapperClassName="absolute -right-1 -top-1.5"
                  />
                </span>
              ) : (
                "—"
              )}
            </td>
            <td className="px-4 py-4">
              {[item.warehouse_name, item.warehouse_location_name]
                .filter(Boolean)
                .join(" · ") || "—"}
            </td>
            <td className="px-4 py-4">
              {item.quantity.toLocaleString("en-CA")} {item.unit}
            </td>
            <td className="px-4 py-4 font-medium">{formatMoney(item.value)}</td>
            <td className="px-4 py-4">
              {item.complete ? (
                <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                  Complete
                </span>
              ) : (
                <FinanceIncompleteBadge count={1} />
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
